// ============================================================
// MyGene — Shared atomic components
// These tiny components enforce consistent patterns across
// every page. Import from 'components/shared'.
// ============================================================

import React, { ReactNode } from "react";
import { Card, Tag, Empty, Spin, Typography } from "antd";
import { InboxOutlined } from "@ant-design/icons";

const { Text, Title } = Typography;

// ── SectionCard ───────────────────────────────────────────────
// Standard content card used on every page.
// Always use this instead of raw <Card> for page sections.
//
// Usage:
//   <SectionCard title="Patient Info" extra={<Button>Edit</Button>}>
//     ...
//   </SectionCard>

interface SectionCardProps {
  title?: ReactNode;
  subtitle?: string;
  extra?: ReactNode;
  children: ReactNode;
  /** Remove inner padding — useful for tables that need edge-to-edge */
  noPadding?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const SectionCard: React.FC<SectionCardProps> = ({
  title, subtitle, extra, children, noPadding, className, style,
}) => (
  <Card
    className={className}
    style={style}
    styles={{
      header: { borderBottom: "1px solid var(--mg-border)" },
      body:   { padding: noPadding ? 0 : 16 },
    }}
    title={
      title
        ? <div style={{ display: "flex", flexDirection: "column", gap: 2, padding: "12px 0" }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--mg-text)" }}>{title}</span>
            {subtitle && <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>{subtitle}</Text>}
          </div>
        : undefined
    }
    extra={extra}
  >
    {children}
  </Card>
);

export default SectionCard;